"use client";

import { Package } from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { routes } from "@/utils/routes";
import Base404 from "./base-404";

type Product404Props = {
  productId?: string;
  productHandle?: string;
};

/**
 * Product-specific 404 page for when a product doesn't exist
 * Provides relevant recovery options for supplement shopping
 */
const Product404: FC<Product404Props> = ({ productId, productHandle }) => {
  const productIdentifier = productHandle || productId;

  return (
    <Base404>
      {/* Product Icon */}
      <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100">
        <Package className="h-8 w-8 text-orange-600" />
      </div>

      {/* Main Heading */}
      <h1 className="heading-display font-bold font-heading text-4xl sm:text-5xl lg:text-6xl">
        Product Not Available
      </h1>

      {/* Subheading */}
      <h2 className="heading-section mt-4 font-bold font-heading text-muted-foreground text-xl sm:text-2xl">
        This product isn't currently in our catalogue
      </h2>

      {/* Description */}
      <p className="mx-auto mt-6 max-w-2xl text-balance text-lg text-muted-foreground text-optimal sm:text-xl">
        The product you're looking for may be out of stock, discontinued, or the
        link may be incorrect. Browse our current selection of quality
        supplements from trusted brands.
      </p>

      {/* Product Info */}
      {productIdentifier && (
        <div className="mt-6">
          <span className="inline-flex items-center rounded-full bg-muted px-3 py-1 font-normal text-muted-foreground text-sm">
            Product: {productIdentifier}
          </span>
        </div>
      )}

      {/* Primary Actions */}
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
        <Button
          asChild
          className="shadow-lg shadow-primary/20 transition-all hover:shadow-primary/25 hover:shadow-xl"
          size="lg"
        >
          <Link href={routes.shop as Route}>Browse Products</Link>
        </Button>
        <Button asChild size="lg" variant="outline">
          <Link href={routes.contact as Route}>Get Help</Link>
        </Button>
      </div>
    </Base404>
  );
};

export default Product404;
